// =============================================================================
// MenuCard — menu strony w Studio: kazda kategoria w menu, grupy jako dropdowny.
// Sekcja pelnej szerokosci (StudioSection): rzad 1 = wskazowka + AI / z WP / jedna
// grupa; rzad 2 = [Dostepne 1/3, sticky, jedyny wewnetrzny scroll] [Menu glowne /
// Stopka 2/3: drzewo 2 poziomy, DnD @dnd-kit, etykiety grup inline]; stopka =
// „Zapisz menu" (disabled + tooltip, gdy brakuje kategorii; „zapisz mimo to"). Zrodla: AI (static_menu_grouping), nav WP, auto. Licznik
// „N/N kategorii" blokuje zapis, gdy ktoras nie jest nigdzie (chyba ze „mimo to").
// Dane „Dostepnych" ze snapshotu static_site_pages (bez assembleSite); pelne
// zlozenie tylko pod „Odswiez liste" / „Odtworz z WordPressa".
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { DndContext, DragOverlay, PointerSensor, closestCenter, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Menu as MenuIcon, Sparkles, Loader2, RefreshCw, FolderPlus, Trash2, GripVertical, Save, Plus, Wand2, AlertTriangle, Globe } from "lucide-react";
import { StudioSection } from "./StudioSection";
import { AiDateBadge } from "./AiDateBadge";
import { useToast } from "@/hooks/use-toast";
import type { MenuItem, SiteMenu, StaticSiteRow, StaticSiteSettings } from "@/services/staticSite/types";
import { buildMenuAuto, buildMenuFromWp, countMenuItems, menuItemId, validateMenu } from "@/services/staticSite/menuBuilder";
import { listSitePages } from "@/services/staticSite/migrationRunner";
import { scopeFromSnapshot, type MenuScope } from "@/services/staticSite/menuScope";
import { assembleSite } from "@/services/staticSite/siteAssembler";
import { fetchAssetText } from "@/services/staticSite/assetFetchClient";
import { proposeMenuGroups } from "@/services/staticSite/menuAiProposer";

interface Props {
  site: StaticSiteRow;
  onSaveSettings: (patch: Partial<StaticSiteSettings>) => Promise<void>;
  onPatchSite: (patch: Partial<StaticSiteRow>) => Promise<void>;
}

const SOURCE_LABEL: Record<SiteMenu["source"], string> = { ai: "AI", wp: "z WordPressa", manual: "ręczne", auto: "auto" };
const AVAIL = "__available__";

function collectRefs(items: MenuItem[], out = new Set<string>()): Set<string> {
  for (const it of items) { if (it.type === "group") collectRefs(it.children || [], out); else out.add(it.id); }
  return out;
}

function removeById(items: MenuItem[], id: string): MenuItem[] {
  return items.filter((it) => it.id !== id).map((it) => (it.type === "group" ? { ...it, children: (it.children || []).filter((c) => c.id !== id) } : it));
}

export function MenuCard({ site, onSaveSettings, onPatchSite }: Props) {
  const { toast } = useToast();
  const [scope, setScope] = useState<MenuScope | null>(null);
  const [scopeSource, setScopeSource] = useState<"snapshot" | "full" | null>(null);
  const [menu, setMenu] = useState<SiteMenu | null>(site.settings?.menu || null);
  const [tab, setTab] = useState<"main" | "footer">("main");
  const [busy, setBusy] = useState<null | "load" | "ai" | "wp" | "save">(null);
  const [hint, setHint] = useState("");
  const [dirty, setDirty] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [customHref, setCustomHref] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // Dostepne: snapshot z ostatniej analizy (bez assembleSite)
  useEffect(() => {
    let alive = true;
    listSitePages(site.id).then((rows) => { if (alive) { setScope(scopeFromSnapshot(site, rows)); setScopeSource(rows.length ? "snapshot" : null); } }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.id, site.analyzed_at]);

  useEffect(() => { setMenu(site.settings?.menu || null); setDirty(false); }, [site.id, site.settings?.menu]);

  const loadFull = useCallback(async (): Promise<MenuScope> => {
    const model = await assembleSite(site.map_id, { settings: site.settings, fetchWpRest: true, fetchWpSitemap: false });
    setScope(model);
    setScopeSource("full");
    return model;
  }, [site.map_id, site.settings]);

  const list = menu ? (tab === "main" ? menu.main : menu.footer || []) : [];
  const setList = (next: MenuItem[]) => {
    setMenu((m) => (m ? { ...m, [tab]: next, source: "manual" } : { main: tab === "main" ? next : [], footer: tab === "footer" ? next : undefined, source: "manual" }));
    setDirty(true);
  };
  const usedIds = useMemo(() => collectRefs(menu ? [...menu.main, ...(menu.footer || [])] : []), [menu]);
  const usedInMain = useMemo(() => collectRefs(menu?.main || []), [menu]);
  const catTotal = scope?.categories.filter((c) => c.count > 0).length ?? 0;
  const catInMenu = scope ? scope.categories.filter((c) => c.count > 0 && usedInMain.has(menuItemId("category", c.slug))).length : 0;
  const complete = catTotal > 0 && catInMenu === catTotal;

  const available: MenuItem[] = useMemo(() => {
    if (!scope) return [];
    const out: MenuItem[] = [];
    for (const c of scope.categories.filter((x) => x.count > 0)) out.push({ id: menuItemId("category", c.slug), label: `${c.name}`, type: "category", ref: c.slug });
    for (const p of scope.pages.filter((x) => x.kind === "subpage" || x.kind === "legacy_page")) out.push({ id: menuItemId("page", p.path), label: p.title, type: "page", ref: p.path });
    if (scope.author?.path) out.push({ id: menuItemId("author", "author"), label: scope.author.name, type: "author" });
    out.push({ id: menuItemId("home", "home"), label: "Strona główna", type: "home" });
    return out;
  }, [scope]);

  const groupOf = (id: string): string | null => {
    for (const it of menu?.main || []) if (it.type === "group" && it.children?.some((c) => c.id === id)) return it.label;
    if ((menu?.main || []).some((it) => it.id === id)) return "poziom 1";
    if ((menu?.footer || []).some((it) => it.id === id || it.children?.some((c) => c.id === id))) return "stopka";
    return null;
  };

  // --- akcje zrodel --------------------------------------------------------
  const applyMenu = (m: SiteMenu, sc: MenuScope) => {
    const v = validateMenu(m, sc);
    setWarnings(v?.warnings || []);
    if (!v) { toast({ title: "Menu puste po walidacji", variant: "destructive" }); return; }
    setMenu(v.menu);
    setDirty(true);
  };

  const runAuto = async () => {
    const sc = scope || (await loadFull());
    applyMenu(buildMenuAuto(sc), sc);
  };

  const runWp = async () => {
    setBusy("wp");
    try {
      const sc = await loadFull();
      const html = await fetchAssetText(`https://${site.domain}/`, site.website_id);
      const m = buildMenuFromWp(html, sc);
      if (!m) { toast({ title: "Nie rozpoznano menu na stronie WP", description: "Użyj „Wszystkie w jednej grupie” albo AI.", variant: "destructive" }); return; }
      applyMenu(m, sc);
      toast({ title: "Menu odtworzone z WordPressa", description: `${countMenuItems(m.main)} pozycji` });
    } catch (e) {
      toast({ title: "Nie udało się pobrać strony WP", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const runAi = async () => {
    setBusy("ai");
    try {
      const sc = scopeSource === "full" && scope ? scope : await loadFull();
      const res = await proposeMenuGroups({ site, scope: sc, hint });
      applyMenu(res.menu, sc);
      toast({ title: "Grupy zaproponowane", description: `${res.menu.main.filter((i) => i.type === "group").length} grup · $${res.costUsd.toFixed(3)}` });
    } catch (e) {
      toast({ title: "Propozycja AI nieudana", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const refreshList = async () => {
    setBusy("load");
    try { await loadFull(); } catch (e) { toast({ title: "Nie udało się złożyć modelu", description: e instanceof Error ? e.message : String(e), variant: "destructive" }); } finally { setBusy(null); }
  };

  const save = async (force = false) => {
    if (!menu || !scope) return;
    if (!complete && !force) return;
    setBusy("save");
    try {
      const v = validateMenu(menu, scope);
      if (!v) throw new Error("Menu puste");
      const next: SiteMenu = { ...v.menu, source: menu.source === "ai" || menu.source === "wp" ? menu.source : "manual", generated_at: new Date().toISOString() };
      await onSaveSettings({ menu: next });
      if (site.pages_subdomain) await onPatchSite({ needs_redeploy: true });
      setMenu(next);
      setDirty(false);
      toast({ title: "Menu zapisane", description: site.pages_subdomain ? "Kliknij „Wdróż zmiany”, żeby menu trafiło na stronę." : undefined });
    } catch (e) {
      toast({ title: "Nie udało się zapisać", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  // --- edycja drzewa ---------------------------------------------------------
  const addGroup = () => setList([...list, { id: menuItemId("group", `g${Date.now()}`), label: "Nowa grupa", type: "group", children: [] }]);
  const renameGroup = (id: string, label: string) => setList(list.map((it) => (it.id === id ? { ...it, label } : it)));
  const removeItem = (id: string) => setList(removeById(list, id));
  const removeGroup = (id: string) => setList(list.filter((it) => it.id !== id));
  const addCustom = () => {
    const href = customHref.trim();
    const label = customLabel.trim() || href;
    if (!href || !/^(https?:\/\/|\/)/.test(href)) return;
    setList([...list, { id: menuItemId("url", href), label, type: "url", href }]);
    setCustomHref(""); setCustomLabel("");
  };
  const addToList = (item: MenuItem, groupId: string | null, index?: number) => {
    let next = removeById(list, item.id);
    if (groupId) {
      next = next.map((it) => {
        if (it.id !== groupId || it.type !== "group") return it;
        const ch = [...(it.children || [])];
        ch.splice(index ?? ch.length, 0, item);
        return { ...it, children: ch };
      });
    } else {
      next.splice(index ?? next.length, 0, item);
    }
    setList(next);
  };

  const findItem = (id: string): MenuItem | null => available.find((a) => a.id === id) || list.find((i) => i.id === id) || list.flatMap((i) => i.children || []).find((c) => c.id === id) || null;

  const onDragEnd = (ev: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = ev;
    if (!over) return;
    const item = findItem(String(active.id));
    if (!item) return;
    const overId = String(over.id);
    if (overId === AVAIL) { if (item.type !== "group") setList(removeById(list, item.id)); return; }
    // upusc na grupe (kontener) -> na koniec grupy
    const overGroup = list.find((it) => it.type === "group" && it.id === overId);
    if (overGroup && item.type !== "group") { addToList(item, overGroup.id); return; }
    // upusc na pozycje -> przed nia (w jej grupie / na poziomie 1); grupy sortuja sie tylko na poziomie 1
    const topIdx = list.findIndex((it) => it.id === overId);
    if (topIdx >= 0) {
      if (item.type === "group") { const from = list.findIndex((it) => it.id === item.id); if (from >= 0) setList(arrayMove(list, from, topIdx)); return; }
      addToList(item, null, topIdx);
      return;
    }
    for (const g of list) {
      if (g.type !== "group") continue;
      const idx = (g.children || []).findIndex((c) => c.id === overId);
      if (idx >= 0) { if (item.type !== "group") addToList(item, g.id, idx); return; }
    }
  };

  const activeItem = activeId ? findItem(activeId) : null;

  const saveButton = (
    <Button size="sm" onClick={() => void save(false)} disabled={!dirty || !complete || busy !== null}>
      {busy === "save" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Zapisz menu
    </Button>
  );

  return (
    <StudioSection
      icon={MenuIcon}
      title="Menu"
      help="Każda kategoria z wpisami musi być w menu głównym. Grupa = nagłówek rozwijanego menu bez własnego adresu. Adresy kategorii się nie zmieniają. Menu trafia na stronę po „Wdróż zmiany”."
      badge={
        <>
          {menu && <AiDateBadge label={SOURCE_LABEL[menu.source]} date={menu.generated_at} />}
          {scope && (
            <Badge variant={complete ? "secondary" : "destructive"} className="text-[10px]" title="Każda kategoria z wpisami musi być w menu głównym">
              {catInMenu}/{catTotal} kategorii w menu
            </Badge>
          )}
        </>
      }
      actions={
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => void refreshList()} disabled={busy !== null} title="Złóż model z WP REST (kilka sekund) — prawdziwe liczby wpisów per kategoria">
          {busy === "load" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />} Odśwież listę
        </Button>
      }
      footer={menu && scope ? (
        <>
          {!complete && <span className="text-xs text-destructive">Brakuje {catTotal - catInMenu} kategorii w menu głównym.</span>}
          {!dirty && site.settings?.menu && <span className="text-xs text-muted-foreground">zapisane</span>}
          {!complete && dirty && <Button size="sm" variant="ghost" onClick={() => void save(true)} disabled={busy !== null}>zapisz mimo to</Button>}
          {!complete && dirty ? (
            <Tooltip>
              <TooltipTrigger asChild><span>{saveButton}</span></TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs">Zapis zablokowany: {catTotal - catInMenu} kategorii nie ma w menu głównym. Przeciągnij je do grupy albo użyj „zapisz mimo to”.</TooltipContent>
            </Tooltip>
          ) : saveButton}
        </>
      ) : undefined}
    >
      <div className="space-y-4">
        {!scope && (
          <p className="text-xs text-muted-foreground">Brak snapshotu stron. Uruchom analizę (sekcja Zgodność) albo „Odśwież listę”.</p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Input value={hint} onChange={(e) => setHint(e.target.value)} placeholder="Wskazówka dla AI, np. 5 grup, osobno narzędzia i case studies" className="h-8 min-w-56 flex-1 text-xs" />
          <Button size="sm" onClick={() => void runAi()} disabled={busy !== null || !scope}>
            {busy === "ai" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Zaproponuj grupy (AI)
          </Button>
          <Button size="sm" variant="outline" onClick={() => void runWp()} disabled={busy !== null} title="Parsuje menu ze strony głównej WordPressa">
            {busy === "wp" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Globe className="mr-2 h-4 w-4" />} Odtwórz z WordPressa
          </Button>
          <Button size="sm" variant="outline" onClick={() => void runAuto()} disabled={busy !== null || !scope} title="Wszystkie kategorie w jednej grupie „Kategorie”">
            <Wand2 className="mr-2 h-4 w-4" /> Jedna grupa
          </Button>
        </div>

        {warnings.length > 0 && (
          <ul className="space-y-0.5 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs">
            {warnings.slice(0, 6).map((w, i) => <li key={i} className="flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-500" /> {w}</li>)}
          </ul>
        )}

        {scope && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="min-w-0 lg:sticky lg:top-4 lg:self-start">
                <AvailablePanel items={available} usedIds={usedIds} groupOf={groupOf} customLabel={customLabel} customHref={customHref} setCustomLabel={setCustomLabel} setCustomHref={setCustomHref} addCustom={addCustom} />
              </div>
              <div className="min-w-0 space-y-2 lg:col-span-2">
                <Tabs value={tab} onValueChange={(v) => setTab(v as "main" | "footer")}>
                  <div className="flex flex-wrap items-center gap-2">
                    <TabsList className="h-8">
                      <TabsTrigger value="main" className="text-xs">Menu główne</TabsTrigger>
                      <TabsTrigger value="footer" className="text-xs">Stopka{menu?.footer?.length ? ` (${countMenuItems(menu.footer)})` : ""}</TabsTrigger>
                    </TabsList>
                    <Button size="sm" variant="outline" className="ml-auto h-8" onClick={addGroup}><FolderPlus className="mr-1 h-3.5 w-3.5" /> Nowa grupa</Button>
                  </div>
                  <TabsContent value={tab} className="mt-2">
                    <MenuTree items={list} renameGroup={renameGroup} removeItem={removeItem} removeGroup={removeGroup} />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
            <DragOverlay>{activeItem ? <div className="rounded border bg-background px-2 py-1 text-xs shadow">{activeItem.label}</div> : null}</DragOverlay>
          </DndContext>
        )}
      </div>
    </StudioSection>
  );
}

// -----------------------------------------------------------------------------
// Dostepne (draggable) + drop-zone „usun z menu"
// -----------------------------------------------------------------------------

function AvailablePanel(props: {
  items: MenuItem[]; usedIds: Set<string>; groupOf: (id: string) => string | null;
  customLabel: string; customHref: string; setCustomLabel: (s: string) => void; setCustomHref: (s: string) => void; addCustom: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: AVAIL });
  const [q, setQ] = useState("");
  const shown = q ? props.items.filter((i) => i.label.toLowerCase().includes(q.toLowerCase())) : props.items;
  return (
    <div ref={setNodeRef} className={`space-y-2 rounded-md border p-2 ${isOver ? "border-primary bg-primary/5" : ""}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium">Dostępne</span>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="szukaj" className="ml-auto h-7 w-full max-w-40 text-xs" />
      </div>
      <ul className="max-h-[70vh] space-y-0.5 overflow-auto">
        {shown.map((it) => <AvailableItem key={it.id} item={it} usedIn={props.usedIds.has(it.id) ? props.groupOf(it.id) : null} />)}
      </ul>
      <div className="flex gap-1 border-t pt-2">
        <Input value={props.customLabel} onChange={(e) => props.setCustomLabel(e.target.value)} placeholder="etykieta" className="h-7 text-xs" />
        <Input value={props.customHref} onChange={(e) => props.setCustomHref(e.target.value)} placeholder="/adres/ lub https://" className="h-7 font-mono text-xs" />
        <Button size="sm" variant="outline" className="h-7" onClick={props.addCustom}><Plus className="h-3.5 w-3.5" /></Button>
      </div>
      <p className="text-[10px] text-muted-foreground">Przeciągnij do menu. Upuszczenie tutaj usuwa pozycję z menu.</p>
    </div>
  );
}

function AvailableItem({ item, usedIn }: { item: MenuItem; usedIn: string | null }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: item.id, disabled: Boolean(usedIn) });
  return (
    <li ref={setNodeRef} {...attributes} {...listeners} className={`flex items-center gap-2 rounded px-2 py-1 text-xs ${usedIn ? "opacity-40" : "cursor-grab hover:bg-accent"} ${isDragging ? "opacity-30" : ""}`}>
      <GripVertical className="h-3 w-3 shrink-0 text-muted-foreground" />
      <Badge variant="outline" className="shrink-0 text-[9px]">{item.type === "category" ? "kat." : item.type}</Badge>
      <span className="truncate">{item.label}</span>
      {usedIn && <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">w: {usedIn}</span>}
    </li>
  );
}

// -----------------------------------------------------------------------------
// Drzewo menu (sortable)
// -----------------------------------------------------------------------------

function MenuTree({ items, renameGroup, removeItem, removeGroup }: { items: MenuItem[]; renameGroup: (id: string, label: string) => void; removeItem: (id: string) => void; removeGroup: (id: string) => void }) {
  if (!items.length) return <div className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">Pusto. Przeciągnij pozycje z lewej albo użyj „Zaproponuj grupy (AI)”.</div>;
  return (
    <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
      <ul className="space-y-1">
        {items.map((it) => it.type === "group"
          ? <GroupNode key={it.id} group={it} renameGroup={renameGroup} removeItem={removeItem} removeGroup={removeGroup} />
          : <LeafNode key={it.id} item={it} removeItem={removeItem} />)}
      </ul>
    </SortableContext>
  );
}

function GroupNode({ group, renameGroup, removeItem, removeGroup }: { group: MenuItem; renameGroup: (id: string, label: string) => void; removeItem: (id: string) => void; removeGroup: (id: string) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group.id });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: group.id });
  const children = group.children || [];
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className={`rounded-md border ${isDragging ? "opacity-40" : ""} ${isOver ? "border-primary" : ""}`}>
      <div className="flex items-center gap-1 border-b bg-muted/30 px-2 py-1">
        <button type="button" className="cursor-grab text-muted-foreground" {...attributes} {...listeners} title="Przeciągnij grupę"><GripVertical className="h-3.5 w-3.5" /></button>
        <Input value={group.label} onChange={(e) => renameGroup(group.id, e.target.value)} className="h-7 flex-1 border-transparent bg-transparent px-1 text-xs font-semibold focus:border-input" maxLength={40} />
        <span className="text-[10px] text-muted-foreground">{children.length}</span>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeGroup(group.id)} title="Usuń grupę (pozycje wracają do Dostępnych)"><Trash2 className="h-3 w-3" /></Button>
      </div>
      <div ref={setDropRef} className="min-h-8 p-1">
        <SortableContext items={children.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          <ul className="space-y-0.5">
            {children.map((c) => <LeafNode key={c.id} item={c} removeItem={removeItem} nested />)}
            {!children.length && <li className="px-2 py-1 text-[10px] text-muted-foreground">upuść tu kategorie</li>}
          </ul>
        </SortableContext>
      </div>
    </li>
  );
}

function LeafNode({ item, removeItem, nested = false }: { item: MenuItem; removeItem: (id: string) => void; nested?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className={`flex items-center gap-2 rounded px-2 py-1 text-xs ${nested ? "" : "border"} ${isDragging ? "opacity-40" : "hover:bg-accent/50"}`}>
      <button type="button" className="cursor-grab text-muted-foreground" {...attributes} {...listeners}><GripVertical className="h-3.5 w-3.5" /></button>
      <Badge variant="outline" className="shrink-0 text-[9px]">{item.type === "category" ? "kat." : item.type}</Badge>
      <span className="truncate">{item.label}</span>
      {item.type === "url" && <span className="truncate font-mono text-[10px] text-muted-foreground">{item.href}</span>}
      <Button size="icon" variant="ghost" className="ml-auto h-6 w-6" onClick={() => removeItem(item.id)} title="Usuń z menu"><Trash2 className="h-3 w-3" /></Button>
    </li>
  );
}
